// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - DAILY LIMITS MANAGER
// Tracks daily purification counts for user retention
// Free: 20 objects/day | Toilet Mode: 50 objects/day
// Resets at midnight UTC
// ═══════════════════════════════════════════════════════════════════════════

const STORAGE_KEY = 'distil_daily_purifications';
const TOILET_STATUS_KEY = 'distil_toilet_holder_status';

export const DAILY_LIMITS = {
  free: 20,
  toiletMode: 50,
};

// ── Core helpers ─────────────────────────────────────────────────────────────

function getToday() {
  return new Date().toISOString().split('T')[0]; // YYYY-MM-DD UTC
}

export function getDailyData() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { date: getToday(), count: 0 };
    const data = JSON.parse(stored);
    if (data.date !== getToday()) return { date: getToday(), count: 0 };
    return data;
  } catch {
    return { date: getToday(), count: 0 };
  }
}

export function incrementDailyCount(amount = 1) {
  const current = getDailyData();
  const updated = {
    date: getToday(),
    count: current.count + amount,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  // 🔔 Dispatch event so DailyLimitIndicator refreshes immediately
  try {
    window.dispatchEvent(new CustomEvent('distil:purge', { detail: updated }));
  } catch {}
  return updated;
}

export function getDailyLimit(isToiletMode = false) {
  return isToiletMode ? DAILY_LIMITS.toiletMode : DAILY_LIMITS.free;
}

export function getDailyUsed() {
  return getDailyData().count;
}

export function getDailyRemaining(isToiletMode = false) {
  const used = getDailyUsed();
  const limit = getDailyLimit(isToiletMode);
  return Math.max(0, limit - used);
}

export function hasReachedLimit(isToiletMode = false) {
  return getDailyRemaining(isToiletMode) === 0;
}

// Restore daily count from Supabase data (call on wallet connect)
export function restoreDailyCountFromSupabase(supabaseCount, supabaseDate) {
  if (!supabaseDate || !supabaseCount) return;
  const today = getToday();
  if (supabaseDate !== today) return; // Different day — local reset is correct
  const local = getDailyData();
  // Use whichever count is higher (prevents cache-clear exploits)
  if (supabaseCount > local.count) {
    const updated = { date: today, count: supabaseCount };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    try {
      window.dispatchEvent(new CustomEvent('distil:purge', { detail: updated }));
    } catch {}
  }
}

// ── Reset countdown ───────────────────────────────────────────────────────────

export function getResetCountdown() {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  tomorrow.setUTCHours(0, 0, 0, 0);
  const diff = tomorrow - now;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

// ── Toilet status cache ───────────────────────────────────────────────────────

export function saveToiletHolderStatus(isHolder) {
  try {
    localStorage.setItem(TOILET_STATUS_KEY, JSON.stringify(!!isHolder));
  } catch {}
}

export function getLastKnownToiletStatus() {
  try {
    const stored = localStorage.getItem(TOILET_STATUS_KEY);
    if (stored === null) return false;
    return JSON.parse(stored) === true;
  } catch {
    return false;
  }
}

// ── Admin override (demo/testing) ─────────────────────────────────────────────

export function resetDailyCount() {
  localStorage.removeItem(STORAGE_KEY);
}
